import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  FiEdit,
  FiTrash2,
  FiClock,
  FiFileText,
  FiUser,
  FiPhone,
  FiGlobe,
  FiUsers,
  FiMail,
  FiCalendar,
  FiShield,
  FiBriefcase,
  FiMapPin,
} from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useState, useEffect } from "react";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { mockPartners, Partner } from "../PartnersPage/mockData";
import { formatDate } from "@/helpers/formatDate";
import { ChakraTabs } from "@/components/tabs/ChakraTabs";

export const SinglePartnerPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadPartner = () => {
      setIsLoading(true);
      const partner = mockPartners.find((p) => p.id === Number(id));
      if (partner) {
        setCurrentPartner(partner);
      }
      setIsLoading(false);
    };
    loadPartner();
  }, [id]);

  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      setIsDeleting(false);
      setDeleteOpen(false);
      navigate("/partners");
    }, 500);
  };

  const getActiveTabIndex = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = ["details", "stack"].indexOf(tabParam);
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0;
  };

  const activeTabIndex = getActiveTabIndex();

  if (isLoading) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Загрузка...</Text>
        </Flex>
      </Layout>
    );
  }

  if (!currentPartner) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Партнер не найден</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate("/partners")} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />
          Назад к партнерам
        </Text>

        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize={30} fontWeight={700} color="#3b515e">
            {currentPartner.name}
          </Text>
          <Flex gap="16px">
            <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate(`/partners/${id}/edit`)}>
              Редактировать
            </Button>
            <Menu>
              <MenuButton leftIcon={<Icon as={FiClock as any} boxSize={4} />} as={Button} variant="outline">
                История
              </MenuButton>
              <MenuList maxH="400px" overflowY="auto" w="400px">
                <Box p="12px">
                  <Text fontSize="sm" color="gray.500">
                    История изменений пуста
                  </Text>
                </Box>
              </MenuList>
            </Menu>
            <Button leftIcon={<Icon as={FiTrash2 as any} boxSize={4} />} colorScheme="red" onClick={() => setDeleteOpen(true)}>
              Удалить
            </Button>
          </Flex>
        </Flex>

        <Flex flexDirection="column" gap="32px">
          <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
            <Tabs index={activeTabIndex} isLazy>
              <ChakraTabs
                tabs={[
                  { id: "details", label: "Детали" },
                  { id: "stack", label: "Стек" },
                ]}
                baseUrl={`/partners/${id}`}
              />
              <TabPanels paddingTop="16px">
                <TabPanel paddingX={0} paddingTop={0}>
                  <Flex flexDirection="column" gap="24px">
                    <ViewInformationBlock icon={<Icon as={FiShield as any} boxSize={5} color="#3b515e" />} title="Основная информация">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <InfoItem label="Название" icon={<Icon as={FiFileText as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.name}
                        </InfoItem>
                        <InfoItem
                          label="Статус"
                          value={
                            <Badge bg="#3B82F6" color="white" px="12px" py="4px" borderRadius="16px" fontSize="14px" fontWeight={500}>
                              {currentPartner.status}
                            </Badge>
                          }
                        />
                        <InfoItem label="Ответственный" icon={<Icon as={FiUser as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.responsible || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Контакты">{currentPartner.contacts || "Не указано"}</InfoItem>
                        <InfoItem label="Рабочий телефон" icon={<Icon as={FiPhone as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.work_phone || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Рабочая почта" icon={<Icon as={FiMail as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.work_email || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Сайт" icon={<Icon as={FiGlobe as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.website || "Не указано"}
                        </InfoItem>
                        <InfoItem label="ИНН">{currentPartner.inn || "Не указано"}</InfoItem>
                        <InfoItem label="Количество сотрудников" icon={<Icon as={FiUsers as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.employees_count || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Формат взаимодействия">{currentPartner.interaction_format || "Не указано"}</InfoItem>
                        <InfoItem label="Комментарий">{currentPartner.comment || "Не указано"}</InfoItem>
                      </SimpleGrid>
                      <Box borderTop="1px solid #E5E7EB" mt="16px" pt="16px" w="100%">
                        <InfoItem label="Дата создания" icon={<Icon as={FiCalendar as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.created_at ? formatDate(currentPartner.created_at) : "Не указано"}
                        </InfoItem>
                      </Box>
                    </ViewInformationBlock>

                    <ViewInformationBlock icon={<Icon as={FiFileText as any} boxSize={5} color="#3b515e" />} title="Юридическая информация">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <InfoItem label="Юридическое название" icon={<Icon as={FiBriefcase as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.legal_name || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Юридический адрес" icon={<Icon as={FiMapPin as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.legal_address || "Не указано"}
                        </InfoItem>
                        <InfoItem label="Почтовый адрес" icon={<Icon as={FiMapPin as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.mailing_address || "Не указано"}
                        </InfoItem>
                        <InfoItem label="ФИО руководителя" icon={<Icon as={FiUser as any} boxSize={4} color="#65758b" />}>
                          {currentPartner.director_name || "Не указано"}
                        </InfoItem>
                      </SimpleGrid>
                    </ViewInformationBlock>

                    <ViewInformationBlock icon={<Icon as={FiFileText as any} boxSize={5} color="#3b515e" />} title="Документы и согласования">
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                        <InfoItem label="Бланк NDA">{currentPartner.has_nda_blank ? "Да" : "Нет"}</InfoItem>
                        <InfoItem label="Скан NDA">{currentPartner.has_nda_scan ? "Да" : "Нет"}</InfoItem>
                        <InfoItem label="Подписано в ЭДО">{currentPartner.signed_in_edo ? "Да" : "Нет"}</InfoItem>
                      </SimpleGrid>
                    </ViewInformationBlock>
                  </Flex>
                </TabPanel>
                <TabPanel paddingX={0} paddingTop={0}>
                  <Text color="#5C657E">Раздел Стек будет добавлен позже</Text>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Flex>
        </Flex>

        <Modal isCentered isOpen={isDeleteOpen} onClose={() => setDeleteOpen(false)}>
          <ModalOverlay backdropFilter="blur(3px)" />
          <ModalContent maxW="50vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
            <ModalHeader p={0}>Подтвердите удаление</ModalHeader>
            <ModalBody>
              <Text>
                Вы действительно хотите удалить партнера <b>{currentPartner?.name}</b>?
              </Text>
            </ModalBody>
            <ModalFooter p={0} pt="8px">
              <Flex gap="12px">
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Отмена
                </Button>
                <Button colorScheme="red" isLoading={isDeleting} onClick={handleDelete}>
                  Удалить
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    </Layout>
  );
};

const InfoItem = ({
  label,
  children,
  icon,
  value,
}: {
  label: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  value?: React.ReactNode;
}) => {
  const displayValue = value || children || "Не указано";

  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Flex alignItems="center" gap="8px">
        {icon && icon}
        <Text color="#65758b" fontSize={14} lineHeight="20px" fontWeight={500}>
          {label}
        </Text>
      </Flex>
      <Text color="#3b515e" fontSize={16} lineHeight="24px" fontWeight={400}>
        {displayValue}
      </Text>
    </Flex>
  );
};
